import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { getAnnouncements, getCoachAttendanceRollupsForGroups, getCoachClasses, getMeCoach } from '@/lib/api-calls';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { validateRouteRole } from '@/lib/routing';
import { isCoach, isEdgeCoach } from '@airreading/common';
import { AnnouncementPlacement, AnnouncementStatus } from '@airreading/common/data';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AnnouncementContent } from '../teachers/announcement/-components/announcement-content';
import CoachStudentGroup from './-components/student-group';
import { WeeklyHoursList } from './-components/weekly-hours';
import { formatCoachAttendanceRollupLine } from '@/lib/student-group-utils';
import { CoachAttendanceStatusLabelMap, CoachAttendanceStatus } from '@airreading/common/data';

export const Route = createFileRoute('/_layout/my-schedule/')({
  beforeLoad: validateRouteRole((user) => isCoach(user) && !isEdgeCoach(user)),
  component: Page,
});

function Page() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load teacher availabilities. Please try again.',
          back: {
            backLink: {
              to: '/',
            },
          },
        };
      }}
    >
      <MySchedulePage />
    </InfoPageSuspenseBoundary>
  );
}

function MySchedulePage() {
  const { data: profile } = useSuspenseQuery(getMeCoach());

  const { data, isPending } = useQuery(
    getAnnouncements({
      pageIndex: 0,
      filters: {
        placements: [AnnouncementPlacement.teacher_my_schedule],
        statuses: [AnnouncementStatus.active],
      },
    }),
  );

  const { data: classes } = useSuspenseQuery({
    ...getCoachClasses(profile.id),
  });

  const { announcements } = data || {};

  const studentGroupIds = (classes ?? []).map((c) => c.id);
  const { data: attendanceMap = {}, isLoading: isAttendanceLoading } = useQuery(
    getCoachAttendanceRollupsForGroups({ coachId: profile.id, studentGroupIds }),
  );

  const filteredClasses = classes.filter((classData) => {
    const hasNextLesson = classData.nextLesson != null;
    const hasValidLearningPath = [
      'Regular',
      'Middle School',
      'Extended',
      'Beginner',
      'Regular - extended',
      'Beginner - extended',
    ].includes(classData.learningPath?.name);
    return hasNextLesson && hasValidLearningPath;
  });

  return (
    <div>
      <InfoPage headerText="My schedule" data-testid="my-schedule-header">
        <div className="absolute top-4 right-10 grid grid-cols-1 gap-4">
          {!isPending &&
            announcements &&
            announcements.length > 0 &&
            announcements.map((announcement) => (
              <div key={announcement.id} className="xl:col-span-2">
                <AnnouncementContent announcement={announcement} />
              </div>
            ))}
        </div>
        <WeeklyHoursList />
      </InfoPage>
      {filteredClasses.length > 0 && (
        <InfoPage headerText="My student groups" data-testid="my-schedule-student-groups-header">
          {filteredClasses.map((classData, idx) => (
            <CoachStudentGroup
              key={classData.id}
              classData={classData}
              teacherTimeZone={profile.timeZone}
              attendanceText={
                isAttendanceLoading ? (
                  <LoaderIcon className="w-5 h-5" />
                ) : (
                  (() => {
                    const { id, v1AbsenceText } = classData;
                    const coachAttendance = formatCoachAttendanceRollupLine(attendanceMap[id]) ?? '-';

                    if (!v1AbsenceText) {
                      return coachAttendance;
                    }

                    const isPerfectAttendance =
                      coachAttendance === CoachAttendanceStatusLabelMap[CoachAttendanceStatus.Perfect];

                    return isPerfectAttendance ? (
                      v1AbsenceText
                    ) : (
                      <div className="flex flex-col">
                        <div>{coachAttendance}</div>
                        <div>{v1AbsenceText}</div>
                      </div>
                    );
                  })()
                )
              }
              data-testid={`my-schedule-student-group-${idx}`}
            />
          ))}
        </InfoPage>
      )}
    </div>
  );
}
